import { useCreateAgendaRecordMutation, useUpdateAgendaRecordMutation, useUpdateWithNestedListMutation } from 'store/services/flow/agendaRecords';
import { type MutableRefObject, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type { TableRow } from 'components/common/Table/tableFunctions';
import { createEmptyRow } from 'components/common/Table/tableFunctions';

import { getAttributeForSave, getCreatedRows, getUpdatedRows } from 'utils/agendaRecord/agendaRecord.ts';
import type { UserAgendaTableDataSchemaType } from 'types/userAgendaTable';
import type { TableRef } from 'components/common/Table/Table';
import { FormValidationException } from '../../error/FormValidationException.ts';
import type { UpdateWithNestedListReq } from 'types/records.ts';

interface UseAgendaTableActionsBaseProps {
  agendaId: string;
  formItemId?: string;
  isNestedList?: boolean;
  value: string[];
  dataSchema: UserAgendaTableDataSchemaType;
  setValue?: (name: string, value: string[], options?: object) => void;
  triggerForm?: (name: string) => void;
  onValueChange?: () => void;
  initialAddedRows?: TableRow<string, UserAgendaTableDataSchemaType>[];
}

/** Only for new record in view detail */
interface UseAgendaTableActionsNewRecordViewDetailProps {
  parentAgendaId: string;
  parentRecordId: string;
}

type UseAgendaTableActionsProps = UseAgendaTableActionsBaseProps &
  ({ parentAgendaId?: never; parentRecordId?: never } | UseAgendaTableActionsNewRecordViewDetailProps);

export const useAgendaTableActions = ({
  agendaId,
  formItemId,
  isNestedList,
  value,
  dataSchema,
  setValue,
  triggerForm,
  onValueChange,
  initialAddedRows,
  parentAgendaId,
  parentRecordId,
}: UseAgendaTableActionsProps) => {
  const { t } = useTranslation();
  const [addedRows, setAddedRows] = useState<TableRow<string, UserAgendaTableDataSchemaType>[]>(
    initialAddedRows && initialAddedRows.length ? initialAddedRows : []
  );
  const [savingData, setSavingData] = useState<boolean>(false);

  const [createAgendaRecord] = useCreateAgendaRecordMutation();
  const [updateAgendaRecord] = useUpdateAgendaRecordMutation();

  const [updateWithNestedList, { isLoading }] = useUpdateWithNestedListMutation();

  const updateNestedList = useCallback(
    (rows: string[], item: string, touch = true) => {
      if (!setValue || !triggerForm) return;

      setValue(item, rows, {
        shouldDirty: touch,
        shouldTouch: touch,
      });
      triggerForm(item);
      onValueChange?.();
    },
    [onValueChange, setValue, triggerForm]
  );

  const resetFormState = useCallback((tableRef: MutableRefObject<TableRef | null>) => {
    setAddedRows([]);
    tableRef.current?.resetSavedForm();
  }, []);

  const handleOnSaveEdit = useCallback(
    async (tableRef: MutableRefObject<TableRef | null>): Promise<void> => {
      const isValid = Boolean(await tableRef.current?.triggerForm());
      if (!isValid) {
        throw new FormValidationException();
      }

      const formData = tableRef.current?.getValues();
      if (!formData) return;

      const dirtyFields = tableRef.current?.getFormState().dirtyFields;
      if (!dirtyFields || !Object.keys(dirtyFields).length) {
        return;
      }

      const createPerRowMap = getCreatedRows(formData, dataSchema);
      const updatePerRowMap = getUpdatedRows(dirtyFields, formData, dataSchema);

      try {
        const updateRecordPromises = Array.from(updatePerRowMap.entries()).map(async ([rowId, attributes]) => {
          await updateAgendaRecord({
            agendaId,
            recordId: rowId,
            body: { status: 'Active', attributes },
          }).unwrap();
        });

        const createdNewRecords: string[] = [];
        let createRecordPromises: Promise<void>[] = [];

        if (parentRecordId && parentAgendaId && formItemId) {
          const nestedListData = Array.from(createPerRowMap.entries()).map(([, attributes]) => {
            return {
              agendaId: agendaId,
              formItemId: formItemId,
              recordId: null,
              attributes: attributes,
            };
          });
          const submitData: UpdateWithNestedListReq = {
            agendaId: parentAgendaId,
            body: {
              attributes: [
                getAttributeForSave(
                  'Records',
                  formItemId,
                  value.map((id) => ({ id }))
                ),
              ],
              recordId: parentRecordId,
              nestedListData,
            },
          };
          const res = await updateWithNestedList(submitData).unwrap();
          res?.nestedRecords?.forEach((record) => {
            if (record.recordId) {
              createdNewRecords.push(record.recordId);
            }
          });
        } else {
          createRecordPromises = Array.from(createPerRowMap.entries()).map(async ([, attributes]) => {
            const res = await createAgendaRecord({
              agendaId,
              body: { status: 'Active', attributes },
            });

            if ('error' in res) throw res.error;

            const newRecordId = res.data?.recordId;
            if (!isNestedList || !newRecordId || !formItemId) return;
            createdNewRecords.push(newRecordId);
          });
        }

        await Promise.all([...updateRecordPromises, ...createRecordPromises]);

        if (formItemId && createdNewRecords.length) {
          updateNestedList([...value, ...createdNewRecords], formItemId);
        }

        resetFormState(tableRef);

        toast.success(t('flow.modulesAndAgendas.form.item.records.updateRowsSuccess'));
      } catch {
        toast.error(t('flow.modulesAndAgendas.form.item.records.updateRowsError'));
      } finally {
        setSavingData(false);
      }
    },
    [
      dataSchema,
      parentRecordId,
      parentAgendaId,
      formItemId,
      resetFormState,
      t,
      updateAgendaRecord,
      agendaId,
      value,
      updateWithNestedList,
      createAgendaRecord,
      isNestedList,
      updateNestedList,
    ]
  );

  const handleRowAdd = useCallback(() => {
    const emptyRow = createEmptyRow(dataSchema);
    setAddedRows((prevState) => [emptyRow, ...prevState]);

    if (!formItemId || !setValue) return;

    setValue(formItemId, [...value, emptyRow.identifier], {
      shouldDirty: true,
      shouldTouch: true,
    });
    onValueChange?.();
  }, [dataSchema, formItemId, onValueChange, setValue, value]);

  const handleCancelEditRow = useCallback(
    (rowId: string) => {
      setAddedRows((prevState) => prevState.filter((row) => row.identifier !== rowId));

      if (!formItemId || !setValue) return;

      const newValue = [...value].filter((id) => id !== rowId);
      setValue(formItemId, newValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
      onValueChange?.();
    },
    [formItemId, onValueChange, setValue, value]
  );

  const handleRowsRemoved = useCallback(
    (removedRowIds: string[]) => {
      if (!formItemId || !setValue) return;

      const newValue = [...value].filter((id) => !removedRowIds.includes(id));
      setValue(formItemId, newValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
      onValueChange?.();
    },
    [formItemId, onValueChange, setValue, value]
  );

  return {
    addedRows,
    handleOnSaveEdit,
    handleRowAdd,
    handleCancelEditRow,
    savingData,
    updateNestedList,
    handleRowsRemoved,
    isTableActionLoading: isLoading,
  };
};
